
import React, { useEffect } from 'react';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Col, DatePicker, Drawer, Form, Input, InputNumber, Select, Switch, Row, Space } from 'antd';
import { FormInstance } from 'antd/es/form/Form';

import { ALL_RULES_CONFIG, SKU_TYPE_OPTIONS, SERVICE_OPTIONS, CURRENCY_OPTIONS, DISCOUNT_TYPE_OPTIONS, CAMPAIGN_TYPE_OPTIONS } from '@/utils/constants';

interface Props {
    showItemCreation: boolean;
    isEditJourney: boolean;
    createItemCallback?: (status: boolean) => void;
    closeItemCreation: () => void;
    initialValues: any;
    form: FormInstance<any>;
    formSubmithandler: (values: any) => void;
}

const { Option } = Select;

// ...existing code...

const CreateCampaigns: React.FC<Props> = ({
    showItemCreation,
    closeItemCreation,
    isEditJourney,
    initialValues,
    form,
    formSubmithandler
}) => {
    useEffect(() => {
        if (isEditJourney && initialValues) {
            // Transform the rules_config object into an array for Form.List
            const rulesConfig = initialValues.rules_config || {};
            const rulesArray = Object.keys(rulesConfig).map(key => {
                // Skip country/currency as it's handled separately
                if (key === 'country') return null;

                const ruleDef = ALL_RULES_CONFIG.find(r => r.value === key);
                let value = rulesConfig[key];

                if (ruleDef?.type === 'boolean') {
                    // For platform rules (e.g., 'platform_ios'), check if the value is the platform name ('ios').
                    // If so, set the form value to `true` to check the Switch.
                    if (key.startsWith('platform_') && value === key.substring('platform_'.length)) {
                        value = true;
                    } else {
                        // For other boolean rules, check for 'true' or the boolean true.
                        value = value === 'true' || value === true;
                    }
                } else if (ruleDef?.type === 'number') {
                    // Convert string numbers from the payload to actual numbers for the InputNumber component.
                    value = Number(value);
                } else if ((key === 'sku_type' || key === 'service') && Array.isArray(value)) {
                    // Keep multi-select rules as array - no transformation needed
                } else if ((key === 'sku_type' || key === 'service') && typeof value === 'string') {
                    // Handle case where multi-select rules come as comma-separated string, convert to array
                    value = value.split(',').map(item => item.trim()).filter(item => item.length > 0);
                }
                return { rule: key, value: value };
            }).filter(item => item !== null); // Remove null items (currency)

            // Handle currency separately
            let currencyValue: string[] = [];
            if (rulesConfig.country) {
                if (Array.isArray(rulesConfig.country)) {
                    currencyValue = rulesConfig.country;
                } else if (typeof rulesConfig.country === 'string') {
                    currencyValue = rulesConfig.country.split(',').map((item: string) => item.trim()).filter((item: string) => item.length > 0);
                }
            }

            form.setFieldsValue({
                ...initialValues,
                rules_config: rulesArray,
                currency: currencyValue,
            });
        } else {
            form.resetFields();
        }
    }, [form, initialValues, isEditJourney, showItemCreation]);

    // Helper function to render the correct input based on the selected rule
    const renderRuleInput = (fieldName: number) => {
        return (
            <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) =>
                    prevValues.rules_config?.[fieldName]?.rule !== currentValues.rules_config?.[fieldName]?.rule
                }
            >
                {({ getFieldValue }) => {
                    const ruleKey = getFieldValue(['rules_config', fieldName, 'rule']);
                    const ruleConfig = ALL_RULES_CONFIG.find(r => r.value === ruleKey);

                    if (!ruleConfig) {
                        return <Input placeholder="Select a rule first" disabled />;
                    }

                    const commonProps = {
                        style: { width: '100%' },
                        placeholder: `Enter value for ${ruleConfig.label}`
                    };

                    switch (ruleConfig.type) {
                        case 'number':
                            return (
                                <Form.Item
                                    name={[fieldName, 'value']}
                                    rules={[{ required: true, message: 'Value is required' }]}
                                    noStyle
                                >
                                    <InputNumber {...commonProps} />
                                </Form.Item>
                            );
                        case 'boolean':
                            // The Switch is disabled. Its value is managed by the onChange handler of the rule selection dropdown.
                            return (
                                <Form.Item
                                    name={[fieldName, 'value']}
                                    valuePropName="checked"
                                    noStyle
                                >
                                    <Switch disabled />
                                </Form.Item>
                            );
                        case 'text':
                        default:
                            // Special handling for multi-select dropdown rules
                            if (ruleKey === 'sku_type') {
                                return (
                                    <Form.Item
                                        name={[fieldName, 'value']}
                                        rules={[{ required: true, message: 'Please select at least one SKU type' }]}
                                        noStyle
                                    >
                                        <Select
                                            mode="multiple"
                                            placeholder="Select SKU types"
                                            style={{ width: '100%' }}
                                        >
                                            {SKU_TYPE_OPTIONS.map(option => (
                                                <Option key={option.value} value={option.value}>{option.label}</Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                );
                            }

                            if (ruleKey === 'service') {
                                return (
                                    <Form.Item
                                        name={[fieldName, 'value']}
                                        rules={[{ required: true, message: 'Please select at least one service' }]}
                                        noStyle
                                    >
                                        <Select
                                            mode="multiple"
                                            placeholder="Select services"
                                            style={{ width: '100%' }}
                                        >
                                            {SERVICE_OPTIONS.map(option => (
                                                <Option key={option.value} value={option.value}>{option.label}</Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                );
                            }

                            if (ruleKey === 'country') {
                                return (
                                    <Form.Item
                                        name={[fieldName, 'value']}
                                        rules={[{ required: true, message: 'Please select at least one country' }]}
                                        noStyle
                                    >
                                        <Select
                                            mode="multiple"
                                            placeholder="Select countries"
                                            style={{ width: '100%' }}
                                        >
                                            {CURRENCY_OPTIONS.map(option => (
                                                <Option key={option.value} value={option.value}>{option.label}</Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                );
                            }

                            return (
                                <Form.Item
                                    name={[fieldName, 'value']}
                                    rules={[{ required: true, message: 'Value is required' }]}
                                    noStyle
                                >
                                    <Input {...commonProps} />
                                </Form.Item>
                            );
                    }
                }}
            </Form.Item>
        );
    };

    const onFinish = (values: any) => {
        // Transform the rules array back into an object with string values before submitting
        const submissionValues = { ...values };
        if (submissionValues.rules_config) {
            const rulesObject = submissionValues.rules_config.reduce((acc: any, item: any) => {
                if (item && item.rule) {
                    // **FIX**: Special handling for platform-specific boolean rules
                    if (item.rule.startsWith('platform_') && item.value === true) {
                        // For 'platform_ios', 'platform_web', etc., use the platform name as the value
                        acc[item.rule] = item.rule.substring('platform_'.length);
                    } else if ((item.rule === 'sku_type' || item.rule === 'service') && Array.isArray(item.value)) {
                        // **FIX**: Special handling for multi-select rules - convert array to comma-separated string
                        acc[item.rule] = item.value.join(',');
                    } else {
                        // **FIX**: For all other rules, convert their value to a string as required by the payload
                        acc[item.rule] = String(item.value);
                    }
                }
                return acc;
            }, {});

            // Add currency field to rules_config only if currency has values
            if (submissionValues.currency && Array.isArray(submissionValues.currency) && submissionValues.currency.length > 0) {
                rulesObject['country'] = submissionValues.currency.join(',');
            }

            submissionValues.rules_config = rulesObject;
        }

        // Remove currency from top level since it's now in rules_config
        delete submissionValues.currency;

        formSubmithandler(submissionValues);
    };


    return (
        <>
            <Drawer
                title={`${isEditJourney ? "Update" : "Create New"} Campaign`}
                placement='right'
                onClose={closeItemCreation}
                visible={showItemCreation}
                destroyOnClose={true}
                size='default'
                width={1000}
            >
                <Form
                    form={form}
                    layout='vertical'
                    onFinish={onFinish}
                >
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label='Name'
                                name='name'
                                rules={[{ required: true, message: 'Enter name' }]}
                            >
                                <Input placeholder='Enter Name' disabled={isEditJourney} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label='Sub title'
                                name='sub_title'
                                rules={[{ required: false }]}
                            >
                                <Input placeholder='Enter Sub title' />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label='Sub title (Hindi)'
                                name='sub_title_hi'
                                rules={[{ required: false }]}
                            >
                                <Input placeholder='Enter Sub title in Hindi' />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label='Description'
                                name='description'
                                rules={[{ required: false }]}
                            >
                                <Input placeholder='Enter Description' />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label='Description (Hindi)'
                                name='description_hi'
                                rules={[{ required: false }]}
                            >
                                <Input placeholder='Enter Description in Hindi' />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label='Start Date'
                                name='start_date'
                                rules={[{ required: true, message: 'Pick Date' }]}
                            >
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label='End Date'
                                name='end_date'
                                rules={[{ required: true, message: 'Pick Date' }]}
                            >
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="discount_type"
                                label="Discount Type"
                                rules={[{ required: true, message: 'Enter discount type' }]}
                            >
                                <Select placeholder="Select a option" allowClear>
                                    {DISCOUNT_TYPE_OPTIONS.map(option => (
                                        <Option key={option.value} value={option.value}>{option.label}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="campaign_type"
                                label="Campaign Type"
                                rules={[{ required: true, message: 'Select campaign type' }]}
                            >
                                <Select placeholder="Select campaign type" allowClear>
                                    {CAMPAIGN_TYPE_OPTIONS.map(option => (
                                        <Option key={option.value} value={option.value}>{option.label}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label='Coupon Value'
                                name='value'
                                rules={[
                                    { required: true, message: 'Enter value' },
                                    ({ getFieldValue }) => ({
                                        validator(_, value) {
                                            const discountType = getFieldValue('discount_type');
                                            if (discountType === 'percentage' && value > 100) {
                                                return Promise.reject('Value should not be greater than 100 for percentage discount');
                                            }
                                            return Promise.resolve();
                                        },
                                    }),
                                ]}
                            >
                                <InputNumber placeholder='Enter value' style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Form.Item
                            noStyle
                            shouldUpdate={(prevValues, currentValues) =>
                                prevValues.discount_type !== currentValues.discount_type
                            }
                        >
                            {({ getFieldValue }) => {
                                const discountType = getFieldValue('discount_type');

                                if (discountType === 'percentage') {
                                    return (
                                        <Col span={12}>
                                            <Form.Item
                                                label='Max Amount'
                                                name='max_amount'
                                                rules={[
                                                    { required: true, message: 'Max amount is required for percentage discount' },
                                                    {
                                                        validator(_, value) {
                                                            // If value exists, check if it's an integer
                                                            if (value && !Number.isInteger(value)) {
                                                                return Promise.reject('Max amount must be an integer value');
                                                            }
                                                            return Promise.resolve();
                                                        }
                                                    }
                                                ]}
                                            >
                                                <InputNumber placeholder='Enter max amount' style={{ width: '100%' }} precision={0} />
                                            </Form.Item>
                                        </Col>
                                    );
                                }
                                return null;
                            }}
                        </Form.Item>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="currency"
                                label="Currency"
                            // rules={[{ required: true, message: 'Please select currency' }]}
                            >
                                <Select
                                    mode="multiple"
                                    placeholder="Select currencies"
                                    style={{ width: '100%' }}
                                >
                                    {CURRENCY_OPTIONS.map(option => (
                                        <Option key={option.value} value={option.value}>{option.label}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <h3 style={{ marginTop: '20px', marginBottom: '10px' }}>Campaign Rules</h3>

                    <Form.List name="rules_config">
                        {(fields, { add, remove }) => (
                            <>
                                {fields.map(({ key, name, ...restField }) => (
                                    <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                                        <Form.Item
                                            noStyle
                                            shouldUpdate={(prevValues, currentValues) =>
                                                prevValues.rules_config !== currentValues.rules_config
                                            }
                                        >
                                            {({ getFieldValue }) => {
                                                const rulesConfigValues = getFieldValue('rules_config') || [];
                                                const allSelectedRules = rulesConfigValues.map((item: any) => item?.rule).filter(Boolean);
                                                const currentRuleValue = getFieldValue(['rules_config', name, 'rule']);

                                                return (
                                                    <Form.Item
                                                        {...restField}
                                                        name={[name, 'rule']}
                                                        rules={[{ required: true, message: 'Missing rule type' }]}
                                                        style={{ width: '250px' }}
                                                    >
                                                        <Select
                                                            placeholder="Select Rule"
                                                            onChange={(value) => {
                                                                const ruleConfig = ALL_RULES_CONFIG.find(r => r.value === value);
                                                                // Set the value to `true` for boolean types to check the switch, otherwise clear it.
                                                                const newValue = ruleConfig?.type === 'boolean' ? true : undefined;
                                                                form.setFieldValue(['rules_config', name, 'value'], newValue);
                                                            }}
                                                        >
                                                            {ALL_RULES_CONFIG.map(rule => (
                                                                <Option
                                                                    key={rule.value}
                                                                    value={rule.value}
                                                                    disabled={allSelectedRules.includes(rule.value) && rule.value !== currentRuleValue}
                                                                >
                                                                    {rule.label}
                                                                </Option>
                                                            ))}
                                                        </Select>
                                                    </Form.Item>
                                                );
                                            }}
                                        </Form.Item>

                                        <div style={{ width: '300px' }}>
                                            {renderRuleInput(name)}
                                        </div>

                                        <MinusCircleOutlined onClick={() => remove(name)} />
                                    </Space>
                                ))}
                                <Form.Item>
                                    <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                                        Add Rule
                                    </Button>
                                </Form.Item>
                            </>
                        )}
                    </Form.List>

                    <Form.Item label="Active status" name="active" valuePropName="checked" style={{ paddingTop: '20px' }}>
                        <Switch />
                    </Form.Item>

                    <Form.Item style={{ paddingTop: '30px' }}>
                        <Button type='primary' htmlType="submit">
                            {isEditJourney ? 'Update Campaign' : 'Create Campaign'}
                        </Button>
                    </Form.Item>
                </Form>
            </Drawer>
        </>
    )
}

export default CreateCampaigns;